import React from "react";
import {
  List,
  Datagrid,
  TextField,
  NumberField,
  FunctionField,
  ReferenceField,
  ReferenceInput,
  AutocompleteInput,
  SelectInput,
  TextInput,
  type ListProps,
} from "react-admin";
import type { RaRecord } from "react-admin";
import { formatINR } from "@/utils";
import { formatDisplayDateTime } from "@/utils/date-format";
import { formatFmbHijriPeriod } from "@/utils/hijri-date-utils";

const contributionTypeChoices = [
  { id: "ZABIHAT", name: "Zabihat" },
  { id: "VOLUNTARY", name: "Voluntary" },
];

export default function FmbContributionsList(_props: ListProps) {
  const filters = [
    <TextInput
      key="q"
      source="q"
      label="Search beneficiary or remarks"
      alwaysOn
      sx={{ minWidth: 260 }}
    />,
    <ReferenceInput source="fmbId" reference="fmbData" key="fmbId" alwaysOn perPage={25}>
      <AutocompleteInput
        fullWidth
        optionText={(choice) =>
          `${choice.fileNo ?? "—"} · ITS ${choice.itsNo ?? "—"} · ${choice.name ?? ""}`.trim()
        }
        filterToQuery={(searchText) => ({ q: searchText })}
        shouldRenderSuggestions={(val: string) => val.trim().length >= 2}
        noOptionsText="No matching FMB records"
        label="Search by HOF ITS..."
        sx={{ minWidth: 300 }}
        debounce={300}
      />
    </ReferenceInput>,
    <ReferenceInput
      source="categoryId"
      reference="fmbContributionCategory"
      key="categoryId"
      alwaysOn
      perPage={100}
      sort={{ field: "sortOrder", order: "ASC" }}
    >
      <SelectInput optionText="name" label="Category" sx={{ minWidth: 180 }} />
    </ReferenceInput>,
    <SelectInput
      source="contributionType"
      key="contributionType"
      choices={contributionTypeChoices}
      label="Legacy Type"
      sx={{ minWidth: 160 }}
    />,
  ];

  return (
    <List
      sort={{ field: "createdAt", order: "DESC" }}
      filters={filters}
      perPage={25}
      title="FMB Contributions"
    >
      <Datagrid rowClick="edit" bulkActionButtons={false}>
        <ReferenceField
          source="fmbId"
          reference="fmbData"
          label="HOF ITS"
          link="show"
          sortable={false}
        >
          <TextField source="itsNo" />
        </ReferenceField>
        <ReferenceField source="fmbId" reference="fmbData" label="HOF Name" sortable={false}>
          <TextField source="name" label="Name" />
        </ReferenceField>
        <FunctionField
          label="Category"
          render={(record) => record?.category?.name ?? record?.contributionType ?? "—"}
        />
        <FunctionField
          label="Hijri period"
          sortBy="hijriYearStart"
          render={(record) => formatFmbHijriPeriod(record?.hijriYearStart, null) ?? "—"}
        />
        <TextField source="beneficiaryItsNo" label="Beneficiary ITS" />
        <TextField source="beneficiaryName" label="Beneficiary name" emptyText="—" />
        <NumberField source="quantity" />
        <FunctionField
          label="Unit amount"
          textAlign="right"
          sortBy="unitAmount"
          render={(record) => formatINR(record?.unitAmount, { empty: "—" })}
        />
        <FunctionField
          label="Amount"
          textAlign="right"
          sortBy="amount"
          render={(record) => formatINR(record?.amount, { empty: "—" })}
        />
        <FunctionField
          label="Created"
          sortBy="createdAt"
          render={(record: RaRecord) => formatDisplayDateTime(record?.createdAt, { empty: "—" })}
        />
      </Datagrid>
    </List>
  );
}
